"use client";

import { Search, User, ShoppingCart, Menu, X } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLoggedInUserId } from "../../api/GetLoggedInUserId";
import { useState, useEffect } from "react";
import LoginRequiredModal from "../item/LoginRequiredModal";

// SOFTCAT 디자인 시스템 상단 바 — 얇은 룰러 스타일.
// 시각 구조는 디자인 산출물을 그대로 따르고, 검색·마이페이지·장바구니 등
// 실동작 기능은 기존 동작을 유지한다.
//
// 페이지로 가는 메뉴는 <button onClick> 이 아니라 <Link>(= <a href>) 로 둔다.
// 버튼은 검색엔진이 따라가지 못해서, 메뉴가 전부 버튼이던 동안 사이트 안의
// 페이지끼리 연결이 없는 것으로 보였다. 사이트맵으로만 페이지를 찾고 있었다.
export default function Header() {
    const { auth, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: memberId, isLoading, isError } = useLoggedInUserId();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // 라우트가 바뀌면 모바일 메뉴를 닫는다
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 900) setIsMenuOpen(false);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const needsLogin = !memberId || isLoading || isError;

    const guard = (path) => {
        if (needsLogin) setIsLoginModalOpen(true);
        else navigate(path);
    };

    const go = (path) => {
        navigate(path);
        setIsMenuOpen(false);
    };

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <header className="sch-top">
                <div className="sch-top__in">
                    <Link
                        to="/"
                        className="sch-top__logo"
                        onClick={() => setIsMenuOpen(false)}
                        style={{ all: "unset", cursor: "pointer", display: "flex" }}
                        aria-label="소프트캣 홈"
                    >
                        <img src="/images/softcat_new_logo2.png" alt="SOFTCAT" />
                    </Link>

                    <nav>
                        <Link to="/about" className={isActive("/about") ? "sch-active" : ""}>회사</Link>
                        <Link to="/portfolio" className={isActive("/portfolio") ? "sch-active" : ""}>포트폴리오</Link>
                        <Link to="/blog" className={isActive("/blog") ? "sch-active" : ""}>블로그</Link>
                        <Link to="/custom-product-first" className={isActive("/custom-product-first") ? "sch-active" : ""}>맞춤 개발</Link>
                        <Link to="/service" className={isActive("/service") ? "sch-active" : ""}>고객지원</Link>
                    </nav>

                    <button className="sch-top__ibtn" aria-label="검색" onClick={() => go("/search")}>
                        <Search className="sc-btn__ico" size={19} />
                    </button>
                    <button className="sch-top__ibtn" aria-label="마이페이지" onClick={() => guard("/mypage")}>
                        <User className="sc-btn__ico" size={19} />
                    </button>
                    <button className="sch-top__ibtn" aria-label="장바구니" onClick={() => guard("/cart")}>
                        <ShoppingCart className="sc-btn__ico" size={19} />
                    </button>

                    <div className="sch-auth">
                        {auth.role === "ADMIN" && (
                            <button onClick={() => go("/admin")}>관리자</button>
                        )}
                        {auth.isLoggedIn ? (
                            <button onClick={logout}>로그아웃</button>
                        ) : (
                            <>
                                <button onClick={() => go("/login")}>로그인</button>
                                <button onClick={() => go("/signup")}>회원가입</button>
                            </>
                        )}
                    </div>

                    <button className="sch-top__cta" onClick={() => go("/custom-product-first")}>
                        무료 견적 받기
                    </button>

                    <button
                        className="sch-top__ibtn sch-top__ham"
                        aria-label="메뉴"
                        aria-expanded={isMenuOpen}
                        onClick={() => setIsMenuOpen((v) => !v)}
                    >
                        {isMenuOpen ? <X size={21} /> : <Menu size={21} />}
                    </button>
                </div>
            </header>

            <div className={`sch-mmenu${isMenuOpen ? " sch-open" : ""}`}>
                <Link to="/" onClick={() => setIsMenuOpen(false)}>홈</Link>
                <Link to="/about" onClick={() => setIsMenuOpen(false)}>회사 소개</Link>
                <Link to="/portfolio" onClick={() => setIsMenuOpen(false)}>포트폴리오</Link>
                <Link to="/blog" onClick={() => setIsMenuOpen(false)}>블로그</Link>
                <Link to="/custom-product-first" onClick={() => setIsMenuOpen(false)}>무료 견적 받기</Link>
                <div className="sch-sub">
                    {auth.isLoggedIn ? (
                        <button onClick={() => { logout(); setIsMenuOpen(false); }}>로그아웃</button>
                    ) : (
                        <>
                            <button onClick={() => go("/login")}>로그인</button>
                            <button onClick={() => go("/signup")}>회원가입</button>
                        </>
                    )}
                    <Link to="/service" onClick={() => setIsMenuOpen(false)}>고객지원</Link>
                    {auth.role === "ADMIN" && <button onClick={() => go("/admin")}>관리자</button>}
                </div>
            </div>

            <LoginRequiredModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onConfirm={() => {
                    window.scrollTo(0, 0);
                    navigate("/login");
                }}
            />
        </>
    );
}
